import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Field,
  reduxForm,
  formValueSelector,
  change as changeFieldValue,
} from "redux-form";
import { FormattedMessage, intlShape } from "react-intl";

import { CheckboxInput, SelectInput } from "../../../common/form";

import EXPORT_FORMATS, { SYMBILITY } from "./enums/exportMemberFormats";

import validationUtils from "../../../../utils/ValidationUtils";
import { EXPORT_MEMBERS_VALIDATION_RULES } from "./exportMembersValidationRules";
import LANGUAGE_LIST from "../../../common/enums/languages";
import userHasAccess from "../../../../utils/userHasAccess";

const formName = "exportMembersForm";

class ExportMembersForm extends React.Component {
  handleClientChange(event) {
    this.resetRecoveryDateField();
    this.props.changeFieldValue(formName, "billingPeriodId", null);
    this.props.changeFieldValue(formName, "languageCode", null);
    this.props.handleClientChange(event);
  }

  resetRecoveryDateField() {
    this.props.changeFieldValue(
      formName,
      "mustHaveMemberIdentifierRecoveryDate",
      false
    );
  }

  render() {
    const { fileFormat, invalid, exporting } = this.props;
    const disableSubmit = invalid || exporting;
    const selectedClient = this.props.clients.find(
      (client) => client.clientId == this.props.clientId
    );
    const selectedClientBillingPeriodType = selectedClient
      ? selectedClient.clientBillingPeriodType
      : "noSelection";
    const showExportWithoutId = selectedClient
      ? selectedClient.shouldImportWithoutId
      : false;

    let exportFormats;
    switch (selectedClientBillingPeriodType) {
      case "noSelection": {
        exportFormats = [];
        break;
      }
      case "RECURRING": {
        exportFormats = EXPORT_FORMATS.filter(
          (format) => !format.manualBillingOnly
        );
        break;
      }
      default: {
        exportFormats = EXPORT_FORMATS.filter((format) => !format.isSpecificForClient);
        const specificFormat = EXPORT_FORMATS.find((format) => format.isSpecificForClient && selectedClient.name.includes(format.value));

        // If the client has a particular export format, we add it to the list
        if (typeof specificFormat !== 'undefined') {
          exportFormats.push(specificFormat);
        }
        break;
      }
    }
    exportFormats = exportFormats.filter((format) => {
      if (format.allowedRoles) {
        return format.allowedRoles.some((role) => userHasAccess(role));
      }
      return true;
    });

    return (
      <form onSubmit={this.props.handleSubmit}>
        <Field
          id="clientId"
          name="clientId"
          component={SelectInput}
          searchable={true}
          options={this.props.clients.map((client) => {
            return { value: client.clientId, text: client.name };
          })}
          addBlankOption={true}
          props={{ onChange: this.handleClientChange.bind(this) }}
          label={
            <FormattedMessage id="importExport.exportMembers.form.client" />
          }
        />

        <Field
          id="billingPeriodId"
          name="billingPeriodId"
          component={SelectInput}
          sortOptions={false}
          options={this.props.billingPeriods.map((billingPeriod) => {
            return {
              value: billingPeriod.billingPeriodId,
              text: `${billingPeriod.localizations[this.props.authLanguageCode].name} (${this.context.intl.formatDate(
                billingPeriod.startDate,
                {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  timeZone: "utc",
                }
              )} ${this.context.intl.formatMessage({
                id: "common.toDate",
              })} ${this.context.intl.formatDate(billingPeriod.endDate, {
                year: "numeric",
                month: "numeric",
                day: "numeric",
                timeZone: "utc",
              })})`,
            };
          })}
          addBlankOption={true}
          label={
            <FormattedMessage id="importExport.exportMembers.form.billingPeriod" />
          }
        />

        <Field
          id="languageCode"
          name="languageCode"
          component={SelectInput}
          options={selectedClient ? LANGUAGE_LIST : []}
          addBlankOption={true}
          props={{ onChange: changeFieldValue }}
          label={
            <FormattedMessage id="importExport.exportInsurers.form.languageCode" />
          }
        />

        <Field
          id="fileFormat"
          name="fileFormat"
          component={SelectInput}
          options={exportFormats}
          addBlankOption={true}
          props={{ onChange: this.resetRecoveryDateField.bind(this) }}
          label={
            <FormattedMessage id="importExport.exportMembers.form.fileFormat" />
          }
        />

        {showExportWithoutId && fileFormat === SYMBILITY.value && (
          <Field
            id="mustHaveMemberIdentifierRecoveryDate"
            name="mustHaveMemberIdentifierRecoveryDate"
            component={CheckboxInput}
            type="checkbox"
            checkbox={{ value: true }}
            label={
              <FormattedMessage id="importExport.exportMembers.form.mustHaveMemberIdentifierRecoveryDate" />
            }
          />
        )}

        <div className="import-export-submit">
          <span className="import-export-button">
            <button
              type="submit"
              className="btn btn-lg btn-primary"
              disabled={disableSubmit}
            >
              <FormattedMessage id="importExport.actions.export" />
            </button>
          </span>

          {exporting && (
            <span className="import-export-loader">
              <span className="loader">
                <FormattedMessage id="importExport.exporting" />
              </span>
            </span>
          )}
        </div>
      </form>
    );
  }
}

ExportMembersForm.propTypes = {
  changeFieldValue: PropTypes.func,
  clientId: PropTypes.string,
  clients: PropTypes.array,
  billingPeriods: PropTypes.array,
  exporting: PropTypes.bool,
  fileFormat: PropTypes.string,
  handleClientChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  invalid: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  user: PropTypes.object,
  authLanguageCode: PropTypes.string,
};

ExportMembersForm.contextTypes = {
  intl: intlShape,
};

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
  };
}

function mapStateToProps(state) {
  return {
    authLanguageCode: state.auth.user.languageCode,
  };
}

ExportMembersForm = reduxForm({
  form: formName,
  validate: (values) =>
    validationUtils.validate(values, EXPORT_MEMBERS_VALIDATION_RULES),
})(ExportMembersForm);

const selector = formValueSelector(formName);

ExportMembersForm = connect(
  (state) => {
    return {
      clientId: selector(state, "clientId"),
      fileFormat: selector(state, "fileFormat"),
    };
  },
  null,
  null,
  { withRef: true }
)(ExportMembersForm);

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(
  ExportMembersForm
);



// WEBPACK FOOTER //
// ./src/components/dashboard/importExport/exportMembers/ExportMembersForm.js